import * as React from "react";
import { InputProps } from "../../interfaces/props/InputProps";
import { TooltipHost, Stack, IStackTokens, TagPicker, ITag, Label, Spinner, SpinnerSize, FontIcon } from "@fluentui/react";
import { DatasourceService } from "../../services/DatasourceService";
import { connect } from "react-redux";
import { RootState } from "../../redux";
import { DataSourcePanel } from "./datasourcePanel";
import { INameValuePairIndex } from "../../interfaces/INameValuePairIndex";
import { DatasourceHelper } from "../helpers/dataSourceHelper";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { IDatasourceQueryDto } from "../../interfaces/IDatasourceQueryDto";
import { setDatasourceValues } from "../../redux/modules/datasource";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { CMAddDatasourceItemPanel } from "../../../../contentManager/Panels/CMAddDatasourceItemPanel";
import { sortTerms } from "../helpers/tagHelper";
import { refrehOptionsRequest } from "../../config/datasourceConstants";

const stackTokens: IStackTokens = {
    childrenGap: 5
};

const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
        return false;
    }
    return tagList.some(compareTag => compareTag.key === tag.key);
};

const getTextFromItem = (item: ITag) => item.name;

const mapDispatchToProps = {
    setDatasourceValues,
};

const mapStateToProps = (state: RootState) => {
    return {
        datasourceItems: state.tenantInfo.datasources,
        currentDirectory: state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null
    };
};

type reduxDispatch = typeof mapDispatchToProps;
type ReduxProps = ReturnType<typeof mapStateToProps>;

interface DatasourceTextTagPickerProps extends InputProps, reduxDispatch, ReduxProps { }

export const UnconnectedDatasourceTextTagPicker: React.FunctionComponent<DatasourceTextTagPickerProps> = ({
    displayName,
    isRequired,
    inputValue,
    inputValidator,
    updateInputValidationStatus,
    error,
    datasourceItems,
    setDatasourceValues,
    currentDirectory,
    containerDisplayNames
}: DatasourceTextTagPickerProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchInputs, setSearchInputs] = React.useState<INameValuePairIndex[]>([]);
    const [boundedField, setBoundedField] = React.useState<string>("");
    const [boundName] = React.useState<string>(inputValue?.xml?.ContentTypeInputName ?? displayName);
    const dataSourceService = new DatasourceService();
    const dataSourceHelper = new DatasourceHelper(dataSourceService);
    const [dataSourceItem] = React.useState(
        DatasourceHelper.getInputDataSource(datasourceItems, boundName, [containerDisplayNames?.siteDisplayName ?? currentDirectory?.siteDisplayName ?? ""], [containerDisplayNames?.libraryDisplayName ?? currentDirectory?.DocumentLibrary ?? currentDirectory?.sharepointList ?? ""])
    );
    const [selectedTags, setSelectedTags] = React.useState<ITag[]>([]);
    const [staticTags, setStaticTags] = React.useState<ITag[] | undefined>();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [enableAutoFillRelatedFields, setEnableAutoFillRelatedFields] = React.useState<boolean>(false);
    const [isAddDatasourceItemPanelOpen, setIsAddDatasourceItemPanelOpen] = React.useState(false);
    const [isMasterDatasource] = React.useState<boolean>(dataSourceItem && boundName && dataSourceItem?.boundFields.find(item => item.name?.split(",").some(name => name.trim().toLowerCase() === boundName.trim().toLowerCase()))?.isMaster);

    const cachedSuggestions = React.useRef<ITag[] | undefined>(undefined);
    const isFirstRun = React.useRef(true);

    React.useEffect(() => {
        if (!isMasterDatasource && Array.isArray(inputValue?.updatedDatasourceOptions) && dataSourceItem?.boundFields?.some(item => item.isMaster)) {
            try {
                const allowedOptions: ITag[] = inputValue.updatedDatasourceOptions.map(option => ({ key: option, name: option }));
                setStaticTags(allowedOptions);
            } catch (e) {
                logger(e, "datasourceTextTagPicker.tsx (updatedDatasourceOptions)");
            }
        }
    }, [inputValue?.updatedDatasourceOptions]);

    React.useEffect(() => {
        if (!isFirstRun.current && !isMasterDatasource) {
            try {
                setStaticTags(undefined);
                cachedSuggestions.current = undefined;
            } catch (e) {
                logger(e, "datasourceTextTagPicker.tsx (resetDatasourceOptionsToggle)");
            }
        }
    }, [inputValue?.resetDatasourceOptionsToggle]);

    React.useEffect(() => {
        const setupAsyncWrapper = async () => {
            setIsLoading(true);
            try {
                const result = await dataSourceHelper.getSearchStructure(dataSourceItem);
                if (Array.isArray(result)) {
                    if (result.length > 1) setEnableAutoFillRelatedFields(true);
                    setSearchInputs(result.map((item, index) => ({ name: item, value: null, index: index })));
                }
                const boundedFieldName = DatasourceHelper.getBoundedField(dataSourceItem, boundName);
                setBoundedField(boundedFieldName ?? "");

                if (inputValue?.value && boundedFieldName && isMasterDatasource) {
                    getRelatedDatasourceValues(inputValue.value, boundedFieldName, false, true);
                }
            } catch (error) {
                logger(error, "datasourceTextTagPicker.tsx (useEffect 1)");
            }
            setIsLoading(false);
        };
        setupAsyncWrapper();
    }, []);

    React.useEffect(() => {
        try {
            const newSelectedTag: ITag = inputValue?.value
                ? {
                    name: inputValue.value as string,
                    key: inputValue.value as string,
                }
                : null;

            setSelectedTags(newSelectedTag ? [newSelectedTag] : []);

            if (isFirstRun.current) {
                isFirstRun.current = false;
                return;
            }

            updateSearch(newSelectedTag?.key as string ?? "");
            updateInputValidationStatus(inputValidator.validate(inputValue?.value));

            if (inputValue?.value && boundedField && isMasterDatasource) {
                getRelatedDatasourceValues(inputValue.value, boundedField, true, true);
            }
        } catch (e) {
            logger(e, "datasourceTextTagPicker.tsx (useEffect 2)");
        }
    }, [inputValue?.updatedFromDataSourceToggle, inputValue?.value]);

    const filterSuggestedTags = async (filterText: string, tagList: ITag[]): Promise<ITag[]> => {
        const getAllSuggestions: boolean = filterText === "*";
        let suggestedTags: ITag[] = [];
        if (filterText?.trim()) {
            if (Array.isArray(staticTags)) {
                return staticTags
                    .filter((tag, index, tags) => {
                        const tagName: string = tag?.name?.toLowerCase();
                        return tagName?.length > 0
                            && (getAllSuggestions || tagName.indexOf(filterText.trim().toLowerCase()) !== -1)
                            && tags.findIndex(t => (t.name === tag.name)) === index
                            && !listContainsTagList(tag, tagList)
                    });
            }
            else {
                try {
                    let result: any[];
                    if (getAllSuggestions && Array.isArray(cachedSuggestions.current) && cachedSuggestions.current.length > 0) {
                        result = cachedSuggestions.current;
                    }
                    else {
                        const request: IDatasourceQueryDto = {
                            id: dataSourceItem.id,
                            name: dataSourceItem.name,
                            tenantId: dataSourceItem.tenantId,
                            typeId: dataSourceItem.type.id,
                            typeName: dataSourceItem.type.name,
                            queryParams: !getAllSuggestions
                                ? [{ name: boundedField, value: filterText.trim() }]
                                : null,
                            returnOnlyQueriedProperties: true,
                            allowCache: dataSourceItem.allowCache
                        };
                        result = await dataSourceService.Search(request);
                    }

                    if (result.length > 0) {
                        const boundFieldProperty: string = Object.keys(result[0]).find(key => key.toLowerCase().trim() === boundedField.toLowerCase().trim());
                        if (boundFieldProperty) {
                            if (getAllSuggestions && (!Array.isArray(cachedSuggestions.current) || cachedSuggestions.current.length === 0)) {
                                cachedSuggestions.current = result;
                            }

                            suggestedTags = result
                                .map<ITag>(resultRow => {
                                    const propertyValue = resultRow[boundFieldProperty]?.trim();
                                    return {
                                        name: propertyValue,
                                        key: propertyValue
                                    }
                                })
                                .filter((tag, index, tags) => {
                                    const tagName: string = tag?.name?.toLowerCase();
                                    return tagName?.length > 0
                                        && (getAllSuggestions || tagName.indexOf(filterText.trim().toLowerCase()) !== -1)
                                        && tags.findIndex(t => (t.name === tag.name)) === index
                                        && !listContainsTagList(tag, tagList)
                                });
                        }
                    } else {
                        if (dataSourceItem.allowAdd) {
                            suggestedTags.push({
                                name: "Add new item",
                                key: "addNewItem"
                            });
                        }
                    }
                } catch (error) {
                    logger(error, "datasourceTextTagPicker.tsx (filterSuggestedTags)");
                }
            }
        }
        return sortTerms(filterText, suggestedTags);
    };

    const onEmptyResolveSuggestions = async (selectedItems?: ITag[]): Promise<ITag[]> => {
        return await filterSuggestedTags("*", selectedItems);
    }

    const closePanel = () => {
        setIsOpen(false);
    };

    const updateInputValue = (newValue: string) => {
        updateInputValidationStatus(inputValidator.validate(newValue));
        updateSearch(newValue);
    };

    const updateSearchInputs = (index: number, value: string) => {
        DatasourceHelper.updateSearchInputs(index, value, searchInputs, setSearchInputs);
    };

    const openPanel = async () => {
        updateSearch(inputValue?.value);
        setIsOpen(true);
    };

    const updateSearch = (newValue: string) => {
        const index = searchInputs.findIndex(item => item.name?.trim().toLowerCase() === boundedField?.trim().toLowerCase());
        updateSearchInputs(index, newValue);
    };

    const onChangeHandle = (tags: ITag[]): void => {
        const searchValue = tags?.length === 1 ? tags[0].key as string : "";
        setSelectedTags(tags);
        updateSearch(searchValue);
        updateInputValidationStatus(inputValidator.validate(searchValue));
        if (enableAutoFillRelatedFields) {
            if (isMasterDatasource && selectedTags?.length > 0) { // Request a reset of associated datasources if value is cleared
                resetRelatedDatasourceValues();
            }
            else {
                getRelatedDatasourceValues(searchValue, boundedField, true);
            }
        }
    };

    const resetRelatedDatasourceValues = async () => {
        const result = await dataSourceHelper.getSearchStructure(dataSourceItem);
        if (Array.isArray(result)) {
            let resetObject = {};
            result.forEach(resultRow => {
                resetObject[resultRow] = refrehOptionsRequest;
            });
            setDatasourceValues(resetObject, false, dataSourceItem?.id);
        }
    }

    const getRelatedDatasourceValues = async (searchValue: string, boundedField: string, isStrict: boolean, initialLoad?: boolean) => {
        if (searchValue?.trim()) {
            try {
                const request: IDatasourceQueryDto = {
                    id: dataSourceItem.id,
                    name: dataSourceItem.name,
                    tenantId: dataSourceItem.tenantId,
                    typeId: dataSourceItem.type.id,
                    typeName: dataSourceItem.type.name,
                    queryParams: [{ name: boundedField, value: searchValue.trim() }],
                    returnOnlyQueriedProperties: false,
                    isStrict: isStrict,
                    allowCache: dataSourceItem.allowCache
                };

                const result = await dataSourceService.Search(request);
                if (result.length > 0) {
                    let resultItem: any = result[0];
                    const propertyKeys = resultItem && Object.keys(result[0]); //keys of the data source, not the sharepoint columns
                    if (propertyKeys) {
                        const options: any[] = [];
                        result.forEach(resultRow => {
                            let boundObject = {};
                            propertyKeys
                                .forEach((key) => {
                                    if (resultRow[key] != null) {
                                        const boundField = dataSourceItem?.boundFields?.find(field => {
                                            return field?.datasourceField.toLowerCase().trim() == key.toLowerCase().trim()
                                        });
                                        if (boundField) {
                                            const names = boundField.name.split(",").map(name => name.toLowerCase().trim());
                                            names
                                                .filter(name => name.toLowerCase().trim() !== boundName.toLowerCase().trim())
                                                .forEach(name => {
                                                    boundObject[name] = resultRow[key];
                                                })
                                        } else {
                                            boundObject[key] = resultRow[key];
                                        }
                                    }
                                });
                            options.push(boundObject);
                        });
                        setDatasourceValues(options, initialLoad, dataSourceItem?.id);
                    }
                }
            } catch (error) {
                logger(error, "datasourceTextTagPicker.tsx (getRelatedDatasourceValues)");
            }
        }
    }

    const onItemSelected = (item) => {
        if (item && item.key === 'addNewItem') {
            setIsAddDatasourceItemPanelOpen(true);
            return null; // return null to prevent adding it to the selected items.
        }
        return item;
    };

    return (
        <>
            <Stack horizontal verticalAlign="start" tokens={stackTokens}>
                <Stack.Item grow={1}>
                    <Label required={isRequired()}>{displayName}</Label>
                    <TagPicker
                        disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
                        className="xb-sp-input"
                        removeButtonAriaLabel="Remove"
                        selectedItems={selectedTags}
                        onResolveSuggestions={filterSuggestedTags}
                        onEmptyResolveSuggestions={onEmptyResolveSuggestions}
                        resolveDelay={500}
                        getTextFromItem={getTextFromItem}
                        onChange={onChangeHandle}
                        itemLimit={1}
                        inputProps={{
                            required: isRequired(),
                        }}
                        onItemSelected={onItemSelected}
                    />
                </Stack.Item>
                {inputValue?.xml?.ReadOnly !== SharePointMetadataBooleans.true && <Stack.Item>
                    {isLoading
                        ? <Spinner size={SpinnerSize.small} style={{ paddingTop: 36, marginLeft: -2 }} />
                        : <div className="xb-search-icon-container">
                            <TooltipHost content={`${languageStrings.Search} ${dataSourceItem?.name} ${languageStrings.Datasource}`}>
                                <FontIcon iconName="Search" onClick={openPanel} />
                            </TooltipHost>
                        </div>}
                </Stack.Item>}
            </Stack>
            <DataSourcePanel
                displayName={dataSourceItem?.name}
                closePanel={closePanel}
                isOpen={isOpen}
                datasourceItem={dataSourceItem}
                datasourceService={dataSourceService}
                searchInputs={searchInputs}
                updateSearchInputs={updateSearchInputs}
                updateInputValue={updateInputValue}
                boundedField={boundedField}
                dataSourceHelper={dataSourceHelper}
                dataSourceItem={dataSourceItem}
            />
            {isAddDatasourceItemPanelOpen &&
                <CMAddDatasourceItemPanel
                    panelIsOpen={isAddDatasourceItemPanelOpen}
                    closePanel={() => setIsAddDatasourceItemPanelOpen(false)}
                    displayName={dataSourceItem?.name}
                    dataSourceHelper={dataSourceHelper}
                    dataSourceItem={dataSourceItem}
                />}
        </>

    );
};

export const DatasourceTextTagPicker = connect(mapStateToProps, mapDispatchToProps)(UnconnectedDatasourceTextTagPicker);

